import { getObject, postObject, getOfficeMdBaseUri, deleteObject, getVoid, putObject } from '../ChoiceApi';
import { IntDatabaseIdAndNameModel } from '../Models/IntDatabaseIdAndNameModel';
import { IDatabaseIdModel } from '../Models/DatabaseIdModel';
import { DatabaseIdTypes } from "../../Enums/ChoiceEnums";
import { IResult } from '../../lib/Models/Result';
import { NoTzDate } from '../DateUtils';

export async function getQuestionSet(cmmEpaCallbackId: number): Promise<CoverMyMedsEpaQuestionResponse|undefined>
{
  return await getObject(`api/CoverMyMeds/ePA/QuestionSet/${cmmEpaCallbackId}`);
}

// Used for testing. Gets a set of static data
export async function getTestQuestionSet(cmmEpaCallbackId: number): Promise<CoverMyMedsEpaQuestionResponse|undefined> {
  return await getObject(`api/CoverMyMeds/ePA/Test/QuestionSet/${cmmEpaCallbackId}`);  
}

export async function questionSetSend(questionSetAnswers: ICoverMyMedsEpaQuestionSaveInputs): Promise<IPassedResponse | undefined>
{
  return await postObject("api/CoverMyMeds/ePA/QuestionSetSend", questionSetAnswers);
}

export function getAttachmentUrl(attachmentIdType?: DatabaseIdTypes, attachmentId?: number): (string|undefined)
{
  if (!attachmentIdType || !attachmentId)
    return;

  return `${getOfficeMdBaseUri()}api/CoverMyMeds/ePA/Attachment/${attachmentIdType}/${attachmentId}`;
}

export async function getAnswers(cmmEpaCallbackId: number): Promise<CoverMyMedsEpaQuestionAnswerItem[]|undefined>
{
  return await getObject(`api/CoverMyMeds/ePA/Answers/${cmmEpaCallbackId}`);
}

export async function getTaskItemStatus(taskItemIdType: DatabaseIdTypes, cmmEpaSendId?: number, cmmEpaCallbackId?: number): Promise<ITaskItemStatusModel|undefined>
{
  return await getObject<ITaskItemStatusModel>(`api/CoverMyMeds/ePA/Status/${taskItemIdType}/${cmmEpaSendId ?? ""}/${cmmEpaCallbackId ?? ""}`);
}

export async function cancelEpaRequestByCallbackId(cmmEpaCallbackId: number): Promise<IPassedResponse | undefined>
{
  return deleteObject<IPassedResponse>(`api/CoverMyMeds/ePA/Cancel/Callback/${cmmEpaCallbackId}`);
}

// Start an Electronic Prior Auth process for a prescription
export async function initiateEpaRequest(prescriptionId: number): Promise<IPassedResponse | undefined>
{
    return getObject<IPassedResponse>(`api/CoverMyMeds/ePA/Initiate/${prescriptionId}`);
}

export async function getApprovalInfo(cmmEpaCallbackId: number): Promise<ICoverMyMedsApprovalModel|undefined>
{
    return getObject(`api/CoverMyMeds/ePA/Status/Approved/${cmmEpaCallbackId}`);
}

export async function getDeniedStatusData(cmmEpaCallbackId: number): Promise<ICoverMyMedsDenialModel|undefined>
{
    return getObject(`api/CoverMyMeds/ePA/Status/Denied/${cmmEpaCallbackId}`);
}


export async function getPartiallyDeniedStatusData(cmmEpaCallbackId: number): Promise<ICoverMyMedPartiallyDeniedStatusModel|undefined>
{
    return getObject(`api/CoverMyMeds/ePA/Status/PartiallyDenied/${cmmEpaCallbackId}`);
}

export async function getCancelledStatusData(cmmEpaCallbackId: number): Promise<ICoverMyMedsCancelledModel|undefined>
{
    return getObject(`api/CoverMyMeds/ePA/Status/Cancelled/${cmmEpaCallbackId}`);
}

export async function getClosedStatusData(cmmEpaCallbackId: number): Promise<ICoverMyMedsClosedModel|undefined>
{
    return getObject(`api/CoverMyMeds/ePA/Status/Closed/${cmmEpaCallbackId}`);
}

export function getApprovalAttachmentUrl(cmmEpaCallbackId: number): string
{
  return `${getOfficeMdBaseUri()}api/CoverMyMeds/ePA/Status/Approved/Attachment/${cmmEpaCallbackId}`;
}

export function getQuestionSetAttachmentUrl(cmmEpaCallbackId: number): string
{
  return `${getOfficeMdBaseUri()}api/CoverMyMeds/ePA/QuestionSet/Attachment/${cmmEpaCallbackId}`;
}


export function resendMessage(cmmEpaSendId: number): Promise<IPassedResponse | undefined>
{
  return getObject(`api/CoverMyMeds/ePA/QuestionSet/ReSend/${cmmEpaSendId}`);
}

export async function clearProblemMessage(idType: DatabaseIdTypes, id: number): Promise<IClearProblemMessageResp|undefined>
{
  return await deleteObject(`api/CoverMyMeds/ePA/QuestionSet/ClearProblemMessage/${idType}/${id}`);
}

export async function getEpaDetails(idType: DatabaseIdTypes, id: number): Promise<IEpaDetailsModel|undefined>
{
  return await getObject(`api/CoverMyMeds/ePA/Details/${idType}/${id}`);
}

export async function getPrescriptionDetails(idType: DatabaseIdTypes, id: number): Promise<IPrescriptionDetails|undefined> {
  return await getObject(`api/CoverMyMeds/ePA/Prescription/Details/${idType}/${id}`);
}

// Indicate to the server that we'll want a PDF -- this makes fetching the attachment later quicker
export async function startPdfGeneration(attachmentIdType?: DatabaseIdTypes, attachmentId?: number)
{
  const url = getAttachmentUrl(attachmentIdType, attachmentId);
  if (url)
    return await getVoid(url);
}


export function prettyChoiceText(choice?: CoverMyMedsEpaQuestionChoiceItem): string|undefined
{
    if (!choice)
        return;

    if ("true" === choice?.choiceText || choice?.choiceId.endsWith("_yes"))
        return "Yes";

    if ("false" === choice?.choiceText || choice?.choiceId.endsWith("_no"))
        return "No";

    return choice.choiceText;
}
export interface IPassedResponse {
  passed: boolean, 
  message?: string
}

// Modify the assignee for a given ePA task item
export async function changeAssignee(messageId: string, idType: DatabaseIdTypes, id: number): Promise<IResult|undefined>
{
  return await putObject(`api/CoverMyMeds/ePA/${messageId}/ChangeAssignee/${idType}/${id}`);  
}

export async function completeItem(cmmEpaCallbackId: number): Promise<IResult|undefined>
{
  return await putObject(`api/CoverMyMeds/ePA/CompleteItem/${cmmEpaCallbackId}`);  
}
export async function undoCompleteItem(cmmEpaCallbackId: number): Promise<IResult|undefined>
{
  return await putObject(`api/CoverMyMeds/ePA/UndoCompleteItem/${cmmEpaCallbackId}`);  
}

export async function markCompletedExternally(idType: DatabaseIdTypes, id: number, comment?: string): Promise<IResult|undefined>
{
  return await postObject(
      `api/CoverMyMeds/ePA/CompletedExternally/${idType}/${id}`,
      { comment: comment });  
}

export interface CoverMyMedsEpaQuestionResponse
{
    questionSet: CoverMyMedsEpaQuestionSet, 
    header: CoverMyMedsEpaQuestionHeaderItem, 
    prescriber: CoverMyMedsEpaQuestionSetPrescriber,
    questionList: CoverMyMedsEpaQuestionItem[], 
    choiceList?: CoverMyMedsEpaQuestionChoiceItem[],
    attachmentFileName?: string
}

export interface CoverMyMedsEpaQuestionSet
{
    paReferenceId: string,
    patientId: number,
    referenceNumber: string,
    caseId?: string,
    deadline?: Date,
    medication: string,
    quantity?: number, 
    daysSupply?: number, 
    quantityUnitOfMeasureCode?: string
}

export interface CoverMyMedsEpaQuestionHeaderItem
{
    questionSetId: string , 
    questionSetTitle: string , 
    questionSetDescription: string, 
    primaryTelephone?: string, 
    supportsSms: boolean, 
    attachementRequired: boolean
}

export interface CoverMyMedsEpaQuestionSetPrescriber
{
  npi?: string, 
  lastName?: string, 
  firstName?: string
}

export interface CoverMyMedsEpaQuestionItem
{
    questionSetId?: string, 
    questionId?: string, 
    sequenceNumber?: number, 
    questionText?: string, 
    defaultNextQuestionId?: string, 
    questionType?: CoverMyMedsQuestionType,

    singleComparisonNextQuestionConditions: ISingleComparisonForNextQuestion[],
    rangeComparisonNextQuestionConditions: IRangeComparisonForNextQuestion[]
}

export interface CoverMyMedsEpaQuestionChoiceItem
{
    questionSetId: string, 
    questionId: string, 
    choiceId: string, 
    sequenceNumber: number, 
    choiceText: string, 
    additionalFreeTextIndicatorType?: AdditionalFreeTextIndicatorType, 
    nextQuestionId?: string
}

export interface CmmAttachment
{
  attachmentFileName?: string,
  attachmentBase64?: string,
  attachmentMimeType?: string
}

export interface CmmAnswerValue
{
  answerValue?: string;
  additionalText?: string;
  multiSelectNextQuestionId?: string;
}

export interface CoverMyMedsEpaQuestionAnswerItem
{
    questionSetId: string, 
    questionId: string, 
    questionType?: CoverMyMedsQuestionType,
    answerValues?: CmmAnswerValue[],
    attachmentId?: IntDatabaseIdAndNameModel
}

export enum CoverMyMedsQuestionType
{
    FreeText = 1,
    Numeric = 2,
    Date = 3,
    DateTime = 4,
    SingleSelect = 5,
    MultiSelect = 6
}

export enum AdditionalFreeTextIndicatorType
{
    NotAllowed = 0,  // "NA"
    Mandatory = 1,  // "M"
    Optional = 2,   // "O"
}

export interface ICoverMyMedsEpaQuestionSaveInputs
{
  cmmEpaCallbackId: number, 
  attachmentId?: IDatabaseIdModel,   // Question set attachment
  questionList: IQuesionListItem[]
}

export interface IQuesionListItem
{
  questionSetId: string, 
  questionId: string, 
  questionType?: CoverMyMedsQuestionType, 
  attachmentId?: IDatabaseIdModel,  // // Question attachment
  answerValues?: IAnswerValue []
}
export interface IAnswerValue 
{
  answerValue: string, 
  additionalText?: string
}

export interface ITaskItemStatusModel
{
  taskItemIdType: DatabaseIdTypes,
  post_date?: string,
  message_id?: string,
  relates_to_message_id?: string,
  writtenByUser?: string,
  replyDeadlineUTC?: Date,
  patientName?: string,
  providerName?: string,
  encounterDate?: string,
  writtenDate?: string,
  drugShortName?: string,
  assigneeName?: string,
  ePATaskStatus?: string,
  ePATaskStatusType?: EPATaskStatusType,
  problemDescription?: string,
  cmmEpaCallbackId?: number,
  completed_externally_timestamp?: string,
  completed_externally_user_id?: string,
  completed_externally_comment?: string

}

export enum EPATaskStatusType
{
  // Send status
  Initiated = 1,
  CMMRejected = 2,
  ErrorCallingCMM = 3,
  //Completed = 4,
  Cancelling = 5,
  Requested = 6,
  
  // Callback status
  Open = 101,
  Problem = 102,
  Followup = 103,
  ApprovedPending = 104,
  DeniedPending = 105,
  ClosedPending = 106,
  Cancelled = 107,
  PartiallyDeniedPending = 108,
  CompletedExternally = 109,

  // Compelted callback status
  Approved = 204,
  Denied = 205,
  Closed = 206,  
  PartiallyDenied= 208,  

  // Unknown state
  UnknownState = 1000
}

// Base to ePA status -- can be cancelled or completed status
export interface ICoverMyMedsFinalStatusModel
{
  paCaseId?: string,
  referenceNumber?: string,
  paNote?: string
}
export interface ICoverMyMedsCompletedStatus extends ICoverMyMedsFinalStatusModel
{
  // Attachment
  attachmentFileName?: string,
  attachmentBase64?: string,
  attachmentMimeType?: string
}


export interface ICoverMyMedsApprovalModel extends ICoverMyMedsCompletedStatus
{
    effectiveDate?: Date,
    expirationDate?: Date,
    isEAppealSupported: boolean,
    appealExpirationDate?: Date,
    authorizationNumber?: string,

    // Processor
    businessName?: string,
}

export interface ICoverMyMedsDenialModel extends ICoverMyMedsCompletedStatus
{
  isEAppealSupported: boolean,
  appealExpirationDate?: Date,
}

export interface ICoverMyMedPartiallyDeniedStatusModel extends ICoverMyMedsCompletedStatus
{
    //AuthorizationNumber
    authorizationNumber?: string,

    //AuthorizationDetail -- Do we need?

    //AuthorizationPeriod
    effectiveDate?: Date,
    expirationDate?: Date,

    //Appeal
    isEAppealSupported: boolean,
    appealExpirationDate?: Date,
}

export interface ICoverMyMedsClosedModel extends ICoverMyMedsCompletedStatus
{
  closedReasonCode?: string,
}

export interface ICoverMyMedsCancelledModel extends ICoverMyMedsFinalStatusModel
{
}

export interface IClearProblemMessageResp
{
  currentIdType: DatabaseIdTypes,
  currentId: number
}

export enum CmmSendMessageTransactionType
{
  PAInitiationRequest = 1,
  PACancelRequest = 2,
  PARequest = 3
}

export enum EpaPriorityType
{
  Standard,
  Urgent  
}

export interface IEpaDetailsModel
{
  billingProviderId: number,
  patientId: number,
  prescriptionId: number,
  cmmEpaSendId: number,
  sendMessageId?: string,
  callbackMessageId?: string,

  paReferenceId?: string,
  userId?: string,
  cmmStatusCode?: string,
  cmmStatusCodeType?: string,
  
  responseStatusCode?: number,
  assigneeIdType: DatabaseIdTypes,
  assigneeId?: string,
  paCaseId?: string,

  priority: EpaPriorityType,

  completed_externally_user_id?: string,
  completed_externally_timestamp?: NoTzDate,
  completed_externally_comment?: string
}

export interface IPrescriptionDetails
{
    patientId?: number,
    medication?: string,
    quantity?: number,
    quantityUnitOfMeasureCode?: string,
    daysSupply?: number,
    prescriberFirstName?: string,
    prescriberLastName?: string,
    prescriberNpi?: string,
    referenceNumber?: string,
    deadline?: Date

    epa_processor_type_id?: DatabaseIdTypes,
    epa_processor_id?: string,
    assigneeName?: string
}

export enum ComparisonType
{
    Equal,              // EQ
    GreaterThan,        // GT
    LessThan,           // LT
    GreaterThanOrEqual, // GE
    LessThanOrEqual,    // LE
    NotEqual            // NE
}

interface INextQuestionConditionBase
{
  nextQuestionID: string
}
interface ISingleComparisonForNextQuestion extends INextQuestionConditionBase
{
  comparison: ComparisonType,
  comparisonValue: number,
}

interface IRangeComparisonForNextQuestion extends INextQuestionConditionBase
{
  lowerComparison: ComparisonType,
  lowerComparisonValue: number,
  upperComparison: ComparisonType,
  upperComparisonValue: number
}